// src/utils/encryption.js
// Common encryption utilities for API payloads using Web Crypto API (browser-compatible)

/**
 * Converts ArrayBuffer to Base64 string
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Converts Base64 string to ArrayBuffer
 * Handles both standard and URL-safe base64 encoding
 */
function base64ToArrayBuffer(base64) {
  try {
    // Remove any whitespace, newlines, or control characters
    let cleanBase64 = base64.replace(/\s/g, "");

    // Convert URL-safe base64 to standard base64
    // Replace - with + and _ with /
    cleanBase64 = cleanBase64.replace(/-/g, "+").replace(/_/g, "/");

    // Add padding if needed
    while (cleanBase64.length % 4 !== 0) {
      cleanBase64 += "=";
    }

    const binary = atob(cleanBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (error) {
    console.error("Base64 decoding error:", error);
    console.error("Problematic base64 string:", base64);
    throw new Error(`Failed to decode base64: ${error.message}`);
  }
}

/**
 * Import RSA public key from PEM format
 */
async function importPublicKey(pemKey) {
  // Remove PEM headers and parse
  const pemContents = pemKey
    .replace("-----BEGIN PUBLIC KEY-----", "")
    .replace("-----END PUBLIC KEY-----", "")
    .replace(/\s/g, "");
  const binaryDer = base64ToArrayBuffer(pemContents);

  return await window.crypto.subtle.importKey(
    "spki",
    binaryDer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["encrypt"]
  );
}

/**
 * Encrypts a JSON payload using AES-256-GCM and encrypts the AES key with RSA-4096 public key.
 * @param {Object} payload - The JSON payload to encrypt.
 * @param {string} publicKey - The RSA-4096 public key (PEM format).
 * @returns {Promise<Object>} { ciphertext, authTag, nonce, encryptedKey }
 */
export async function encryptJsonPayload(payload, publicKey) {
  // Generate random AES key (256-bit)
  const aesKey = await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt"]
  );

  // Generate random nonce (96 bits / 12 bytes for GCM)
  const nonce = window.crypto.getRandomValues(new Uint8Array(12));

  // Convert payload to JSON string then to ArrayBuffer
  const jsonString = JSON.stringify(payload);
  const encoder = new TextEncoder();
  const data = encoder.encode(jsonString);

  // Encrypt data with AES-GCM
  const encryptedData = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce },
    aesKey,
    data
  );

  // Extract ciphertext and auth tag (last 16 bytes of GCM output)
  const ciphertextArray = new Uint8Array(encryptedData);
  const ciphertext = ciphertextArray.slice(0, -16);
  const authTag = ciphertextArray.slice(-16);

  // Export AES key to encrypt it with RSA
  const exportedAesKey = await window.crypto.subtle.exportKey("raw", aesKey);

  // Import RSA public key and encrypt the AES key
  const rsaKey = await importPublicKey(publicKey);
  const encryptedSessionKey = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    rsaKey,
    exportedAesKey
  );

  return {
    ciphertext: arrayBufferToBase64(ciphertext),
    authTag: arrayBufferToBase64(authTag),
    nonce: arrayBufferToBase64(nonce),
    encryptedKey: arrayBufferToBase64(encryptedSessionKey),
  };
}

/**
 * Import RSA private key from PEM format (for backend/Node.js only)
 */
async function importPrivateKey(pemKey) {
  const pemContents = pemKey
    .replace("-----BEGIN PRIVATE KEY-----", "")
    .replace("-----END PRIVATE KEY-----", "")
    .replace(/\s/g, "");
  const binaryDer = base64ToArrayBuffer(pemContents);

  return await window.crypto.subtle.importKey(
    "pkcs8",
    binaryDer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["decrypt"]
  );
}

/**
 * Decrypts an encrypted payload using RSA-4096 private key and AES-256-GCM.
 * Note: This is typically done on the backend, not in the browser
 * @param {Object} encryptedData - Object containing { ciphertext, authTag, nonce, encryptedKey }
 * @param {string} privateKey - The RSA-4096 private key (PEM format).
 * @returns {Promise<Object>} The decrypted JSON payload
 */
export async function decryptJsonPayload(encryptedData, privateKey) {
  try {
    // Support both 'encryptedKey' and 'encryptedSessionKey' for compatibility
    const { ciphertext, authTag, nonce, encryptedKey, encryptedSessionKey } =
      encryptedData;

    const sessionKey = encryptedKey || encryptedSessionKey;

    if (!sessionKey || !ciphertext || !authTag || !nonce) {
      throw new Error(
        "Missing required encryption fields. Expected: encryptedKey, ciphertext, authTag, nonce"
      );
    }

    console.log("Decryption inputs:", {
      hasEncryptedKey: !!sessionKey,
      hasCiphertext: !!ciphertext,
      hasAuthTag: !!authTag,
      hasNonce: !!nonce,
      encryptedKeyLength: sessionKey?.length,
      ciphertextLength: ciphertext?.length,
    });

    // Import RSA private key
    const rsaKey = await importPrivateKey(privateKey);

    // Decrypt the AES session key using RSA private key
    const encryptedKeyBuffer = base64ToArrayBuffer(sessionKey);
    const aesKeyBuffer = await window.crypto.subtle.decrypt(
      { name: "RSA-OAEP" },
      rsaKey,
      encryptedKeyBuffer
    );

    // Import the decrypted AES key
    const aesKey = await window.crypto.subtle.importKey(
      "raw",
      aesKeyBuffer,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"]
    );

    // Combine ciphertext and authTag for GCM decryption
    const ciphertextBuffer = base64ToArrayBuffer(ciphertext);
    const authTagBuffer = base64ToArrayBuffer(authTag);
    const combined = new Uint8Array(
      ciphertextBuffer.byteLength + authTagBuffer.byteLength
    );
    combined.set(new Uint8Array(ciphertextBuffer), 0);
    combined.set(new Uint8Array(authTagBuffer), ciphertextBuffer.byteLength);

    // Decrypt the payload using AES-256-GCM
    const nonceBuffer = base64ToArrayBuffer(nonce);
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonceBuffer },
      aesKey,
      combined
    );

    // Convert decrypted data back to JSON
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decryptedBuffer);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("❌ Decryption failed:", error.message);
    console.error("Full error:", error);
    throw error;
  }
}

/**
 * Encrypts a FormData payload using AES-256-GCM and encrypts the AES key with RSA-4096 public key.
 * @param {FormData} formData - The FormData payload to encrypt.
 * @param {string} publicKey - The RSA-4096 public key (PEM format).
 * @returns {Object} { ciphertext, authTag, nonce, encryptedKey }
 */
export async function encryptFormDataPayload(formData, publicKey) {
  // Convert FormData to an object
  const formObj = {};
  for (let [key, value] of formData.entries()) {
    formObj[key] = value;
  }
  return encryptJsonPayload(formObj, publicKey);
}

/**
 * Helper function to get the RSA public key from environment or config
 * @returns {string} The RSA-4096 public key in PEM format
 */
export function getPublicKey() {
  // Hardcoded RSA public key
  const RSA_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----\nMIICIjANBgkqhkiG9w0BAQEFAAOCAg8AMIICCgKCAgEAwL9dTTWU+cgpdxtLON16\nSxvWO/FIERK5TpWv+P+HaEYVoLFAlz/Qv04LytJ8YfoI3l64qTvY7F0VW7FHvxTa\nSHfYPt8NZ79HkycTim2snK0vfzyqiQKblCna87WpZobfZ+A157Lj2iNryof7evVa\nc21C3VHkrdIIOW/85nAz9xaIIIqu9wqqJZ7+Nv6M/SG6O1Cm67wwLb3kpBDgxhR0\nXOVQAizfV2/aHCk7Uy3X2QPWgyK19hQ00B2js38wsNLB8017MGNpZa1R3BrHX3u4\n3R3C9biQGjDMzLwIZi+fGjX6mjDbse0ZKDkwZb5T6h6+UxcASM63zWrij2+FBcxN\nSsvLCyELcOIu3mptpZiT58CcHH11et1xoW8C7hGrOPnM5O8tj9Og5GgY9ctz0qo7\nfCDkHs0jXE2jRCJ1mjrR/rGQRfZvKz/Zj7sVmt6xWNIH4g8/qcMbBp3MOJYBo4AS\n/DJpg0eXuSXlLSTGONvY7zfmjlv3HBHU5EAMpwxuV6vTAvwlSfDU7vhDj+nA0Ozm\nXUj1gTK/7zbPh9YF2eLvfS4cVxB976+HsmhNTUCk7gadPwwQZ/wYX7cFukCyhWxG\nudIoySYeJHZ+Sxk3f+HFLFwkgl5PDaPVerotyAIvUln7fhsCf4xGD17BWtK7SVqX\ngiBheiQwkfdi8pQAmYyO4zsCAwEAAQ==\n-----END PUBLIC KEY-----`;

  return (
    process.env.REACT_APP_RSA_PUBLIC_KEY ||
    localStorage.getItem("rsa_public_key") ||
    RSA_PUBLIC_KEY
  );
}

/**
 * Helper function to get the RSA private key for response decryption
 * @returns {string} The RSA-4096 private key in PEM format
 */
export function getPrivateKey() {
  // Hardcoded RSA private key (matches the public key sent in request headers)
  const RSA_PRIVATE_KEY = `-----BEGIN PRIVATE KEY-----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\nue5Uy3QgYD0Ibt8GKtBq2bPmwHtwAw==\n-----END PRIVATE KEY-----`; 


  return (
    process.env.REACT_APP_RSA_PRIVATE_KEY ||
    localStorage.getItem("rsa_private_key") ||
    RSA_PRIVATE_KEY
  );
}

/**
 * Helper function to decrypt API response if encrypted
 * @param {Object} response - Axios response object
 * @returns {Promise<Object>} Response with decrypted data
 */
export async function decryptResponse(response) {
  try {
    // Check if response is encrypted
    // Some APIs wrap encryption metadata in 'data', others put it at the root
    const body = response?.data;
    const isEncrypted =
      body?.encrypted === true || body?.data?.encrypted === true;

    // Find where the encryption fields (ciphertext, encryptedKey, etc) actually are
    const encryptionData =
      body?.encryptedKey || body?.encryptedSessionKey
        ? body
        : body?.data?.encryptedKey || body?.data?.encryptedSessionKey
        ? body.data
        : null;

    if (isEncrypted && encryptionData) {
      const privateKey = getPrivateKey();

      if (!privateKey) {
        console.warn(
          "⚠️ No private key available for decryption. Returning encrypted response."
        );
        return response;
      }

      console.log("🔓 Decrypting response...");

      // Decrypt the data using the identified encryption source
      const decryptedData = await decryptJsonPayload(encryptionData, privateKey);

      console.log("✅ Response decrypted successfully");

      // Return response with decrypted data
      // We replace the response body with the decrypted payload.
      const finalData =
        typeof decryptedData === "object" && decryptedData !== null
          ? {
              status:
                response.data.status !== undefined
                  ? response.data.status
                  : true,
              ...decryptedData,
            }
          : decryptedData;

      return {
        ...response,
        data: finalData,
      };
    }

    // If not encrypted, return as is
    return response;
  } catch (error) {
    console.error("❌ Error decrypting response:", error);
    console.error("📋 Response data structure:", {
      encrypted: response?.data?.encrypted,
      hasEncryptedKey: !!response?.data?.data?.encryptedKey,
      dataKeys: response?.data?.data
        ? Object.keys(response.data.data)
        : "no data",
    });
    console.error("🔍 Error details:", error.message);
    console.warn("⚠️ Returning encrypted response due to decryption error");

    // Return original response so the app doesn't break
    return response;
  }
}

/**
 * Helper function to extract decrypted data from API response
 * Works with both Redux actions and direct API calls
 * @param {Object} response - API response (can be from Redux action or direct API call)
 * @param {boolean} isReduxAction - True if response is from Redux action (response.payload)
 * @returns {any} The decrypted data
 */
export function extractDecryptedData(response, isReduxAction = false) {
  try {
    // Get the actual response data
    const responseData = isReduxAction
      ? response?.payload?.data
      : response?.data;

    if (!responseData) {
      console.warn("⚠️ No response data found");
      return null;
    }

    // Check if response was encrypted and decrypted
    if (responseData?.encrypted === false && responseData?.data !== undefined) {
      // Decrypted response: responseData.data
      console.log("✅ Extracted decrypted data");
      return responseData.data;
    }

    // Check if it's a direct array or object (not encrypted)
    if (Array.isArray(responseData) || typeof responseData === "object") {
      // Direct response (not encrypted)
      return responseData;
    }

    // Fallback: try to find data in common locations
    return responseData?.data || responseData;
  } catch (error) {
    console.error("❌ Error extracting decrypted data:", error);
    return null;
  }
}
